import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { FC, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Button, { ButtonProps } from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import useBoolean from 'src/hooks/useBoolean';
import useGetInvoiceApprovers, { InvoiceApproverItem } from 'src/services/useGetInvoiceApprovers';
import useSubmitForApprovalApi from 'src/services/useSubmitForApprovalApi';
import InvoiceApproversTable from '../invoice_details/InvoiceApproversTable';

interface SubmitForApprovalDialogProps {
  invoiceIds: string[];
  btnDisabled: boolean;
  className?: string;
  btnColor?: ButtonProps['color'];
  refresh?: () => Promise<any>;
}

const SubmitForApprovalDialog: FC<SubmitForApprovalDialogProps> = ({
  invoiceIds,
  btnDisabled,
  className,
  btnColor,
  refresh,
}) => {
  const { value: isModalOpen, setFalse: handleCloseModal, setTrue: openModal } = useBoolean();
  const [selectedApprover, setSelectedApprover] = useState<InvoiceApproverItem>(
    {} as InvoiceApproverItem
  );

  const { submitForApproval, isSubmittingForApproval } = useSubmitForApprovalApi(
    invoiceIds.map((id) => Number(id)),
    selectedApprover?.clientId ?? 0
  );

  const { invoiceApprovers, isApproversLoading, setInvoiceApprovers, getInvoiceApprovers } =
    useGetInvoiceApprovers();

  const handleSubmit = useCallback(async () => {
    try {
      const response = await submitForApproval();
      if (!(response as any)?.unProcessed?.length) {
        await refresh?.();
        toast.success('Submitted for approval successfully!');
      }
    } finally {
      handleCloseModal();
    }
  }, [handleCloseModal, refresh, submitForApproval]);

  useEffect(() => {
    if (isModalOpen && !invoiceApprovers?.length) {
      getInvoiceApprovers();
    }
  }, [getInvoiceApprovers, invoiceApprovers?.length, isModalOpen]);

  return (
    <>
      <Button
        disabled={btnDisabled}
        loading={isSubmittingForApproval}
        className={className}
        color={btnColor}
        onClick={openModal}
      >
        Submit for Approval
      </Button>
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        title="Select Approver to Submit for approval"
        id="modal"
      >
        <InvoiceApproversTable
          setSelectedApprover={setSelectedApprover}
          invoiceApprovers={invoiceApprovers}
          isApproversLoading={isApproversLoading}
          setInvoiceApprovers={setInvoiceApprovers}
        />
        <DialogActionsBar>
          <div className="flex flex-row items-center justify-end gap-2">
            {invoiceApprovers?.length > 0 && (
              <Button
                color="info"
                onClick={handleSubmit}
                disabled={!selectedApprover?.clientId}
                loading={isSubmittingForApproval}
              >
                Submit for Approval
              </Button>
            )}
            <Button color="info" onClick={handleCloseModal}>
              {invoiceApprovers?.length > 0 ? 'Cancel' : 'OK'}
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>
    </>
  );
};

export default SubmitForApprovalDialog;
